import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MicrositeContent } from '@/types/microsite';
import { micrositeService } from '@/services/microsite.service';
import { ImageCard } from '@/components/microsite/ImageCard';
import { MicrositeNav } from '@/components/microsite/MicrositeNav';

export default function MicrositeBenefitsPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const navigate = useNavigate();
  const [, setData] = useState<MicrositeContent | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shareId) {
      navigate('/p/error', { replace: true });
      return;
    }
    micrositeService
      .getContent(shareId)
      .then(setData)
      .catch(() => navigate('/p/error', { replace: true }))
      .finally(() => setLoading(false));
  }, [shareId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#bd9734] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-screen-sm mx-auto p-4 pb-24 space-y-4">
      <header className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Tee Box Benefits</h1>
        <p className="text-sm text-gray-600">
          Train with purpose — tools, coaching, and environment to make real progress.
        </p>
      </header>

      <section className="grid gap-3">
        <ImageCard
          src="/images/trackman-equipment.webp"
          alt="Trackman equipment"
          headline="Cutting-Edge Technology"
          sub="Trackman data, 3D coaching, improvement plans, and video analysis so you can measure and track progress."
        />
        <ImageCard
          src="/images/coaching.jpg"
          alt="Professional golf coaching at Tee Box simulator"
          headline="World-Class Coaching"
          sub="Box Pro guidance with on-demand reviews, video analysis, and live support when you need it."
        />
        <ImageCard
          src="/images/facility.jpg"
          alt="Facility access"
          headline="24/7 Facility Access"
          sub="Train on your schedule with simulators, 3D technology, gym, and putting green."
        />
      </section>

      <section className="bg-gradient-to-r from-gray-900 to-black rounded-2xl p-6 text-center">
        <h2 className="text-xl font-bold text-white mb-2">Train with Purpose</h2>
        <p className="text-gray-300 text-sm mb-3">
          At Tee Box, you don't just practice—you train with purpose, with the tools, coaching, and
          environment to make real progress.
        </p>
        <a
          href="/contact"
          className="inline-block px-4 py-2 rounded-lg bg-[#bd9734] text-white text-sm font-medium shadow-sm"
        >
          Schedule a visit
        </a>
      </section>

      <MicrositeNav shareId={shareId!} />
    </div>
  );
}
